import { useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { transactionServices } from '../transactionServices'
import { GetTransactionsAdaptedParams, Transaction } from '../transactionTypes'

interface UseGetTransactionsParams
  extends Omit<GetTransactionsAdaptedParams, 'page_id'> {
  enabled?: boolean
}

export const useGetTransactions = (params: UseGetTransactionsParams = {}) => {
  const { enabled = true, ...queryParams } = params

  // Normalize queryKey by converting Date objects to timestamps for proper comparison
  const normalizedQueryKey = useMemo(() => {
    const normalized = { ...queryParams }
    if (normalized.from_date instanceof Date) {
      normalized.from_date = normalized.from_date.getTime()
    }
    if (normalized.to_date instanceof Date) {
      normalized.to_date = normalized.to_date.getTime()
    }
    return ['transactions', normalized]
  }, [queryParams])

  return useInfiniteQuery({
    queryKey: normalizedQueryKey,
    queryFn: async ({ pageParam }) => {
      // queryParams already contains Date objects, use them directly
      const response = await transactionServices.getTransactions({
        ...queryParams,
        page_id: pageParam || 0,
        page_size: queryParams.page_size || 20,
      })

      return {
        transactions: response.transactions,
        next_page_id: response.next_page_id,
      }
    },
    getNextPageParam: (lastPage) => {
      return lastPage.next_page_id || null
    },
    initialPageParam: 0,
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  })
}
